import type {
  TerraApiRoute,
  TerraApiRouteResponses,
  TerraApiSuccessBody,
} from '@terra/shared-utils';

const SUCCESS_STATUS_CODES = [200, 201, 204] as const;

export class SkillsApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'SkillsApiError';
    this.status = status;
    this.body = body;
  }
}

const extractErrorMessage = (status: number, body: unknown): string => {
  if (body && typeof body === 'object') {
    const candidate = body as {
      message?: unknown;
      error?: unknown;
      errors?: unknown;
    };
    if (typeof candidate.message === 'string' && candidate.message.length > 0) {
      return candidate.message;
    }
    if (typeof candidate.error === 'string' && candidate.error.length > 0) {
      return candidate.error;
    }
    if (Array.isArray(candidate.errors) && candidate.errors.length > 0) {
      const firstError = candidate.errors[0] as { message?: unknown };
      if (
        firstError &&
        typeof firstError.message === 'string' &&
        firstError.message.length > 0
      ) {
        return firstError.message;
      }
    }
  }
  if (typeof body === 'string' && body.length > 0) {
    return body;
  }
  return `Request failed with status ${status}`;
};

export async function callSkillsEndpoint<
  TRoute extends TerraApiRoute,
  TResponse extends
    TerraApiRouteResponses<TRoute> = TerraApiRouteResponses<TRoute>,
>({
  endpointFn,
}: {
  endpointFn: () => Promise<TResponse>;
}): Promise<TerraApiSuccessBody<TRoute>> {
  let response: TResponse;
  try {
    response = await endpointFn();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Unknown network error');
  }

  if ((SUCCESS_STATUS_CODES as readonly number[]).includes(response.status)) {
    return response.body as TerraApiSuccessBody<TRoute>;
  }

  throw new SkillsApiError(
    response.status,
    extractErrorMessage(response.status, response.body),
    response.body,
  );
}
